import React, { useState, useEffect, useContext, useRef } from 'react';
import axios from 'axios';

import { styled, alpha } from '@mui/material/styles';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Toolbar from '@mui/material/Toolbar';
import InputBase from '@mui/material/InputBase';
import SearchIcon from '@mui/icons-material/Search';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import InputLabel from '@mui/material/InputLabel';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import ListAltIcon from '@mui/icons-material/ListAlt';
import DeleteIcon from '@mui/icons-material/Delete';

import { RequestContext } from '../../contexts/RequestContext';
import { ListingContext } from '../../contexts/ListingContext';
import { UserContext } from '../../contexts/UserContext';
import { OperatorContext } from '../../contexts/OperatorContext';

import './ManageOperators.css'

import config from '../../config/config';


const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.15),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.25),
  },
  marginLeft: 0,
  width: '100%',
  [theme.breakpoints.up('sm')]: {
    marginLeft: theme.spacing(1),
    width: 'auto',
  },
}));

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    // vertical padding + font size from searchIcon
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      width: '12ch',
      '&:focus': {
        width: '20ch',
      },
    },
  },
}));

function ManageRequests() {
  const { getRequests, requests, deleteRequest, unviewedRequests, markRequestAsViewed, markAllRequestsAsViewed } = useContext(RequestContext)
  const { getListings } = useContext(ListingContext)
  const { getAllUsers } = useContext(UserContext)
  const { getAllOperators } = useContext(OperatorContext)
  const [allListings, setAllListings] = useState([])
  const [allUsers, setAllUsers] = useState([])
  const [allOperators, setAllOperators] = useState([])
  const [dataHidden, setDataHidden] = useState(false)
  const [allData, setAllData] = useState({})
  const [searchString, setSearchString] = useState('')
  const [filteredData, setFilteredData] = useState([])
  const [deleteConfirm, setDeleteConfirm] = useState(null)
  const [listingEdits, setListingEdits] = useState({})
  const [listingLoading, setListingLoading] = useState({})
  const [error, setError] = useState('')
  const [userOperatorEdits, setUserOperatorEdits] = useState({ user: '', organization: '' })
  const [userOperatorLoading, setUserOperatorLoading] = useState({ user: false, organization: false })
  const [forceUpdate, setForceUpdate] = useState(0)
  const [currentRequestData, setCurrentRequestData] = useState(null)
  const isInitialMount = useRef(true)

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 3000)
      return () => clearTimeout(timer)
    }
  }, [error])

  useEffect(() => {
    getRequests()
    getListings(1, 'all').then((data) => { if (data) setAllListings(data) }).catch(() => {})
    getAllUsers(1, undefined, 'all').then((data) => { if (data) setAllUsers(data) })
    getAllOperators(1, null, 'all').then((data) => { if (data) setAllOperators(data) })
  }, [])

  useEffect(() => {
    setFilteredData([...processData(requests)])
  }, [requests])

  // Refetch requests when users are deleted
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }
    // When allUsers changes (user deleted/added), refetch requests
    // since requests are deleted when a user is deleted
    getRequests(searchString)
  }, [allUsers.length, getRequests, searchString])


  function processData(data) {
    const newData = data.map(e => createData(`${e.first_name} ${e.last_name}`, e.email, e.organization, e.request_type, e.status))
    return newData
  }

  function createData(name, email, organization, request_type, status) {
    return { name, email, organization, request_type, status };
  }

  function handleSearch(e) {
    let string = e.target.value
    setSearchString(string)

    if (string !== '') {
      getRequests(string)
    }
    else {
      getRequests()
    }
  }

  function handleCloseForm(e) {
    if (e.target.className === 'modal-overlay') {
      setDataHidden(false)
    }
  }

  function getCurrListData(i) {
    return requests[i]
  }

  function handleSeeRequestInfo(i) {
    let selected = getCurrListData(i)
    setAllData(selected)
    setCurrentRequestData(selected)

    // Mark request as viewed when opened
    markRequestAsViewed(selected._id)

    const initialListingEdits = {}
    if (selected.listings) {
      selected.listings.forEach(listing => {
        const listingId = listing._id || listing.id
        if ((listing.status === 'mapped' || listing.mapped_id || listing.mappedTo) && (listing.mappedTo || listing.mapped_id)) {
          const mappedId = listing.mappedTo || listing.mapped_id
          initialListingEdits[listingId] = {
            mappedListing: allListings.find(f => String(f._id) === String(mappedId)) || null
          }
        }
      })
    }
    setListingEdits(initialListingEdits)
    setUserOperatorEdits({
      user: selected.user_id || '',
      organization: selected.organization_id || ''
    })
    setDataHidden(true)
    setError('')
  }

  const handleMapListing = async (listingIdx, mappedListingId) => {
    if (!allData || !allData._id || !allData.listings) return
    
    const listing = allData.listings[listingIdx]
    const listingId = listing._id || listing.id
    
    if (!listingId) {
      setError('Cannot map listing: No listing ID found')
      return
    }
    
    if (!mappedListingId) {
      setError('Please select a listing to map to')
      return
    }
    
    setListingLoading(l => ({ ...l, [listingIdx]: true }))
    setError('')
    try {
      const response = await axios.put(
        `${config.apiUrl}/api/request/${allData._id}/listing/${listingId}`,
        { action: 'map', mappedTo: mappedListingId }
      )
      
      if (response.data && response.data.success !== false) {
        const updatedListings = [...allData.listings]
        updatedListings[listingIdx] = {
          ...updatedListings[listingIdx],
          mappedTo: mappedListingId,
          mapped_id: mappedListingId,
          rejected: false,
          status: 'mapped'
        }
        const updatedRequest = {
          ...allData,
          listings: updatedListings
        }
        
        setAllData(updatedRequest)
        setCurrentRequestData(updatedRequest)
        setForceUpdate(prev => prev + 1)
        
        // Clear the mappedListing input for this listing
        setListingEdits(edits => ({ ...edits, [listingId]: { ...edits[listingId], mappedListing: null } }))
        
        // Refresh in background
        getRequests(searchString)
      } else {
        throw new Error('Failed to update listing mapping')
      }
    } catch (err) {
      console.error('Listing mapping error:', err)
      setError('Failed to map listing: ' + (err.response?.data?.message || err.message))
    }
    setListingLoading(l => ({ ...l, [listingIdx]: false }))
  }

  const handleRejectListing = async (listingIdx) => {
    if (!allData || !allData._id || !allData.listings) return
    
    const listing = allData.listings[listingIdx]
    const listingId = listing._id || listing.id
    
    if (!listingId) {
      setError('Cannot reject listing: No listing ID found')
      return
    }
    
    setListingLoading(l => ({ ...l, [listingIdx]: true }))
    setError('')
    try {
      const response = await axios.put(
        `${config.apiUrl}/api/request/${allData._id}/listing/${listingId}`,
        { action: 'reject' }
      )
      
      if (response.data && response.data.success !== false) {
        const updatedListings = [...allData.listings]
        updatedListings[listingIdx] = {
          ...updatedListings[listingIdx],
          rejected: true,
          mappedTo: null,
          mapped_id: null,
          status: 'rejected'
        }
        const updatedRequest = {
          ...allData,
          listings: updatedListings
        }
        
        setAllData(updatedRequest)
        setCurrentRequestData(updatedRequest)
        setForceUpdate(prev => prev + 1)
        
        // Refresh in background
        getRequests(searchString)
      } else {
        throw new Error('Failed to update listing rejection')
      }
    } catch (err) {
      console.error('Listing rejection error:', err)
      setError('Failed to reject listing: ' + (err.response?.data?.message || err.message))
    }
    setListingLoading(l => ({ ...l, [listingIdx]: false }))
  }

  const handleMapUser = async (userId) => {
    if (!allData || !allData._id) return
    setUserOperatorLoading(l => ({ ...l, user: true }))
    setError('')
    try {
      await axios.put(`${config.apiUrl}/api/request/${allData._id}/user`, { userId })
      // Immediately update local state
      const updatedRequest = { ...allData, user_id: userId }
      setAllData(updatedRequest)
      setCurrentRequestData(updatedRequest)
      setUserOperatorEdits(edits => ({ ...edits, user: userId }))
      setForceUpdate(prev => prev + 1)
      // Refresh in background
      getRequests(searchString)
    } catch (err) {
      setError('Failed to map user.')
    }
    setUserOperatorLoading(l => ({ ...l, user: false }))
  }
  const handleMapOperator = async (operatorId) => {
    if (!allData || !allData._id) return
    setUserOperatorLoading(l => ({ ...l, organization: true }))
    setError('')
    try {
      await axios.put(`${config.apiUrl}/api/request/${allData._id}/operator`, { operatorId })
      const updatedRequest = { ...allData, organization_id: operatorId }
      setAllData(updatedRequest)
      setCurrentRequestData(updatedRequest)
      setUserOperatorEdits(edits => ({ ...edits, organization: operatorId }))
      setForceUpdate(prev => prev + 1)
      getRequests(searchString)
    } catch (err) {
      setError('Failed to map operator.')
    }
    setUserOperatorLoading(l => ({ ...l, organization: false }))
  }

  const currentData = currentRequestData || allData
  const allListingsMapped = currentData && currentData.listings && currentData.listings.length > 0 &&
    currentData.listings.every(f => {
      const isMapped = !!(f.mapped_id || f.mappedTo)
      const isRejected = !!f.rejected
      // For approval, listing must be mapped, NOT rejected
      return isMapped && !isRejected
    })
  const anyListingRejected = currentData && currentData.listings && currentData.listings.some(f => f.rejected)
  const userMapped = !!(userOperatorEdits.user || currentData?.user_id)
  const operatorMapped = !!(userOperatorEdits.organization || currentData?.organization_id)
  const canApprove = allListingsMapped && userMapped && operatorMapped && !anyListingRejected

  const handleEdit = async (e) => {
    let newData = { ...allData }
    const newStatus = e.target.value
    newData.status = newStatus

    if (newStatus === 'Approved') {
      // Frontend validation before attempting approval
      const currentData = currentRequestData || allData
      const listingsValid = currentData.listings && currentData.listings.length > 0 &&
        currentData.listings.every(f => !!(f.mapped_id || f.mappedTo) && !f.rejected)
      const anyRejected = currentData.listings && currentData.listings.some(f => f.rejected)
      const userValid = !!(currentData.user_id)
      const operatorValid = !!(currentData.organization_id)

      if (anyRejected) {
        setError('Cannot approve: one or more listings have been rejected. All listings must be mapped to approve.')
        return
      }
      if (!listingsValid) {
        setError('All listings must be mapped before approving.')
        return
      }
      if (!userValid) {
        setError('User must be mapped before approving.')
        return
      }
      if (!operatorValid) {
        setError('Operator must be mapped before approving.')
        return
      }
      
      try {
        // Call verification route before approving
        const verifyRes = await axios.post(`${config.apiUrl}/api/request/${allData._id}/verify-approval`)
       
        if (verifyRes.data.success === false) {
          setError(verifyRes.data.message);
          return;
        }
        else {
          setError('')
        }


      } catch (err) {
        console.log(err)
        setError('Failed to verify approval conditions.');
        return;
      }
    }

    try {
      await axios.put(`${config.apiUrl}/api/request/${allData._id}`, { status: newStatus })
      // Immediately update local state
      setAllData(newData)
      setCurrentRequestData(newData)
      setForceUpdate(prev => prev + 1)
      // Refresh in background
      getRequests(searchString)
    } catch (error) {
      console.log(error)
      setError('Failed to update request status: ' + (error.response?.data?.message || error.message))
    }
  }

  function handleDelete() {
    let selected = getCurrListData(deleteConfirm)
    deleteRequest(selected._id, searchString)
    setDeleteConfirm(null)
  }

  return (
    <div>
      <Box sx={{ flexGrow: 1 }}>
        <AppBar style={{ backgroundColor: 'white' }} position="static">
          <Toolbar style={{ display: 'flex', justifyContent: 'space-between' }} >
            <Search>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <StyledInputBase
                placeholder="Search…"
                inputProps={{ 'aria-label': 'search' }}
                onChange={(e) => { handleSearch(e) }}
                value={searchString}
              />
            </Search>
            {unviewedRequests.length > 0 && (
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={markAllRequestsAsViewed}
                style={{ marginLeft: '16px' }}
              >
                Mark All as Viewed ({unviewedRequests.length})
              </Button>
            )}
          </Toolbar>
        </AppBar>
      </Box>

      <TableContainer style={{ marginTop: '30px' }} component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Organization</TableCell>
              <TableCell>Request Type</TableCell>
              <TableCell>Status</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredData.map((row, i) => {
              const currentRequest = requests[i];
              const isUnviewed = currentRequest && unviewedRequests.some(req => String(req._id) === String(currentRequest._id));
              
              return (
                <TableRow
                  key={row.name + i}
                  sx={{ 
                    '&:last-child td, &:last-child th': { border: 0 },
                    backgroundColor: isUnviewed ? '#fff5f5' : 'inherit'
                  }}
                >
                  <TableCell>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      {isUnviewed && (
                        <div 
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            backgroundColor: '#f44336',
                            marginRight: '8px',
                            flexShrink: 0
                          }}
                        />
                      )}
                      {row.name}
                    </div>
                  </TableCell>
                  <TableCell>{row.email}</TableCell>
                  <TableCell>{row.organization}</TableCell>
                  <TableCell>{row.request_type}</TableCell>
                  <TableCell>{row.status}</TableCell>
                  <TableCell>
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      <TableOption
                        color={'primary'}
                        name={'See Info'}
                        onClick={() => { handleSeeRequestInfo(i) }}
                      >
                        <ListAltIcon />
                      </TableOption>

                      <TableOption
                        color={'primary'}
                        name={'Delete'}
                        onClick={() => { setDeleteConfirm(i) }}
                      >
                        <DeleteIcon />
                      </TableOption>
                    </div>
                  </TableCell>

                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={deleteConfirm !== null}
        onClose={() => { setDeleteConfirm(null) }}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Delete Request
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {`Are you sure you want to delete ${filteredData[deleteConfirm] ? filteredData[deleteConfirm].email : ''}?`}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setDeleteConfirm(null) }}>Cancel</Button>
          <Button onClick={() => { handleDelete() }} autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {dataHidden && (() => {
        const mappingDisabled = allData.status === 'Approved' || allData.status === 'Rejected';
        if (mappingDisabled) {
          // Simplified view for approved/rejected
          const mappedUser = allData.user_id ? allUsers.find(u => String(u._id) === String(allData.user_id)) : null;
          const mappedOperator = allData.organization_id ? allOperators.find(o => String(o._id) === String(allData.organization_id)) : null;
          return (
            <div onClick={(e) => { handleCloseForm(e) }} className="modal-overlay">
              <div className="manage-facility-form">
                <div className="request-info-modal-data">
                  <h2 style={{ color: allData.status === 'Approved' ? 'green' : 'red', textAlign: 'center', marginBottom: 24 }}>
                    {allData.status}
                  </h2>
                  <h3>Contact Name</h3>
                  <p>{allData.first_name} {allData.last_name}</p>
                  <h3>Contact Email</h3>
                  <p>{allData.email}</p>
                  <h3>Phone</h3>
                  <p>{allData.phone}</p>
                  <h3>Organization</h3>
                  <p>{allData.organization}</p>
                  <h3>User Mapping</h3>
                  <p>{mappedUser ? `${mappedUser.first_name} ${mappedUser.last_name} (${mappedUser.email})` : 'Not mapped'}</p>
                  <h3>Operator Mapping</h3>
                  <p>{mappedOperator ? mappedOperator.name : (allData.organization_id || 'Not mapped')}</p>
                  <h3>Listings</h3>
                  {(currentRequestData?.listings || allData.listings) && (currentRequestData?.listings || allData.listings).map((e, i) => (
                    <div key={`${e._id || e.id || i}-${forceUpdate}`} style={{ marginBottom: 12, border: '1px solid #eee', padding: 8, borderRadius: 4 }}>
                      <p><b>Name:</b> {e.name}</p>
                      <p><b>Status:</b> {
                        e.rejected ? 'Rejected' :
                        (e.mapped_id || e.mappedTo) ? 'Mapped' :
                        'Pending'
                      }
                        {(e.mapped_id || e.mappedTo) && (
                          <span> (Mapped to: {allListings.find(f => String(f._id) === String(e.mapped_id || e.mappedTo))?.name || (e.mapped_id || e.mappedTo)})</span>
                        )}
                      </p>
                    </div>
                  ))}
                  <Button
                    variant="outlined"
                    color="primary"
                    style={{ marginTop: 24, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                    onClick={async () => {
                      try {
                        await axios.put(`${config.apiUrl}/api/request/${allData._id}`, { status: 'Review' });
                        await getRequests();
                        setAllData(prev => ({ ...prev, status: 'Review' }));
                      } catch (err) {
                        setError('Failed to revert status.');
                      }
                    }}
                  >
                    Revert to In Review
                  </Button>
                  {error && <div style={{ color: 'red', marginTop: 8 }}>{error}</div>}
                </div>
              </div>
            </div>
          );
        }
        // Editable modal (default)
        return (
          <div onClick={(e) => { handleCloseForm(e) }} className="modal-overlay">
            <div className="manage-facility-form">
              <div className="request-info-modal-data">
                <h3>Contact Name</h3>
                <p>{allData.first_name} {allData.last_name}</p>
                <h3>Contact Email</h3>
                <p>{allData.email}</p>
                {/* User Mapping moved here, now searchable */}
                <h3>User Mapping</h3>
                <div style={{ marginBottom: 12 }}>
                  <FormControl size="small" style={{ minWidth: 220, marginRight: 8 }}>
                    <Autocomplete
                      options={allUsers}
                      getOptionLabel={u => u ? `${u.first_name} ${u.last_name} (${u.email})` : ''}
                      value={allUsers.find(u => String(u._id) === String(userOperatorEdits.user)) || null}
                      onChange={(e, val) => setUserOperatorEdits(edits => ({ ...edits, user: val ? val._id : '' }))}
                      renderInput={params => <TextField {...params} label="Select user" variant="outlined" size="small" />}
                      disabled={userOperatorLoading.user || mappingDisabled}
                      isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
                    />
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!userOperatorEdits.user || userOperatorLoading.user || userOperatorEdits.user === allData.user_id || mappingDisabled}
                    onClick={() => handleMapUser(userOperatorEdits.user)}
                  >
                    {userOperatorLoading.user ? 'Mapping...' : 'Map User'}
                  </Button>
                  {allData.user_id && (
                    <span style={{ marginLeft: 12, color: 'green' }}>Mapped to: {allUsers.find(u => String(u._id) === String(allData.user_id))?.first_name || allData.user_id}</span>
                  )}
                </div>
                <h3>Phone</h3>
                <p>{allData.phone}</p>
                <h3>Organization</h3>
                <p>{allData.organization}</p>
                {/* Operator Mapping moved here, now searchable */}
                <h3>Operator Mapping</h3>
                <div style={{ marginBottom: 12 }}>
                  <FormControl size="small" style={{ minWidth: 220, marginRight: 8 }}>
                    <Autocomplete
                      options={allOperators}
                      getOptionLabel={o => o ? o.name : ''}
                      value={allOperators.find(o => String(o._id) === String(userOperatorEdits.organization)) || null}
                      onChange={(e, val) => setUserOperatorEdits(edits => ({ ...edits, organization: val ? val._id : '' }))}
                      renderInput={params => <TextField {...params} label="Select operator" variant="outlined" size="small" />}
                      disabled={userOperatorLoading.organization || mappingDisabled}
                      isOptionEqualToValue={(option, value) => String(option._id) === String(value._id)}
                    />
                  </FormControl>
                  <Button
                    variant="contained"
                    size="small"
                    disabled={!userOperatorEdits.organization || userOperatorLoading.organization || userOperatorEdits.organization === allData.organization_id || mappingDisabled}
                    onClick={() => handleMapOperator(userOperatorEdits.organization)}
                  >
                    {userOperatorLoading.organization ? 'Mapping...' : 'Map Operator'}
                  </Button>
                  {allData.organization_id && (
                    <span style={{ marginLeft: 12, color: 'green' }}>Mapped to: {allOperators.find(o => String(o._id) === String(allData.organization_id))?.name || allData.organization_id}</span>
                  )}
                </div>
                <h3>Listings</h3>
                {(currentRequestData?.listings || allData.listings) && (currentRequestData?.listings || allData.listings).map((e, i) => (
                  <div key={`${e._id || e.id || i}-${forceUpdate}`} style={{ marginBottom: 16, border: '1px solid #eee', padding: 8, borderRadius: 4 }}>
                    <p>name: {e.name}</p>
                    <p>address: {e.address}</p>
                    <p>city,state, zip: {e.city}, {e.state} {e.zip}</p>
                    <p>ccn: {e.CCN}</p>
                    <p>listing id: {e._id || e.id || 'No ID'}</p>
                    <div>
                      <b>Status:</b> {
                        e.rejected ? 'Rejected' :
                        e.mapped_id || e.mappedTo ? 'Mapped' :
                        'Pending'
                      }
                      {(e.mapped_id || e.mappedTo) && (
                        <span> (Mapped to: {allListings.find(f => String(f._id) === String(e.mapped_id || e.mappedTo))?.name || (e.mapped_id || e.mappedTo)})</span>
                      )}
                    </div>
                    {(e.status === 'pending' || e.status === 'mapped' || e.status === 'rejected' || !e.status) && !mappingDisabled ? (
                      <div style={{ marginTop: 8 }}>
                        <FormControl size="small" style={{ minWidth: 180, marginRight: 8 }}>
                          <Autocomplete
                            size="small"
                            options={allListings}
                            getOptionLabel={(option) => option.name || ''}
                            value={
                              listingEdits[e._id || e.id]?.mappedListing ||
                              allListings.find(f => String(f._id) === String(e.mapped_id || e.mappedTo)) ||
                              null
                            }
                            onChange={(_, newValue) => setListingEdits(edits => ({ ...edits, [e._id || e.id]: { ...edits[e._id || e.id], mappedListing: newValue } }))}
                            renderInput={(params) => <TextField {...params} label="Map to Listing" variant="outlined" />}
                            style={{ minWidth: 220, marginRight: 8, opacity: mappingDisabled ? 0.5 : 1 }}
                            disabled={mappingDisabled}
                          />
                        </FormControl>
                        <Button
                          variant="contained"
                          size="small"
                          style={{ marginRight: 8 }}
                          disabled={!listingEdits[e._id || e.id]?.mappedListing || listingLoading[i] || mappingDisabled}
                          onClick={() => handleMapListing(i, listingEdits[e._id || e.id]?.mappedListing?._id)}
                        >
                          {listingLoading[i] ? 'Mapping...' : 'Map'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          color="error"
                          disabled={listingLoading[i] || mappingDisabled}
                          onClick={() => handleRejectListing(i)}
                        >
                          {listingLoading[i] ? 'Rejecting...' : 'Reject'}
                        </Button>
                      </div>
                    ) : null}
                  </div>
                ))}
                <h3>Request Type</h3>
                <p>{allData.request_type}</p>
                {/* Status Selector replaces Approve button */}
                <h3>Status</h3>
                <FormControl size="small" style={{ minWidth: 180, marginTop: 16 }}>
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={allData.status}
                    label="Status"
                    onChange={handleEdit}
                  >
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Review">In Review</MenuItem>
                    <MenuItem
                      value="Approved"
                      disabled={!canApprove}
                    >
                      Approved
                    </MenuItem>
                    <MenuItem value="Rejected">Rejected</MenuItem>
                  </Select>
                </FormControl>
                {error && <div style={{ color: 'red', marginTop: 8, fontWeight: 600 }}>{error}</div>}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  )
}

export default ManageRequests

const TableOption = ({ children, name, color, onClick }) => {
  return (
    <div style={{ margin: '0px 10px', cursor: 'pointer' }} onClick={onClick}>
      <Tooltip title={name}>
        <IconButton color={color}>
          {children}
        </IconButton>
      </Tooltip>
    </div>
  )
}